import React, { useEffect, useState } from 'react';
import { Download, Upload, CheckCircle, RefreshCw, AlertCircle, ShieldAlert, Shield } from 'lucide-react';
import { downloadUserTemplate, processUserList, getPublicContractLicensePlan } from '../firebase/api';
import LicensePlanPanel from '../components/LicensePlanPanel';

/** Solo en local: muestra la carga vacía aunque el expediente ya tenga lista. */
function isUploadPreview() {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') return false;
    return new URLSearchParams(window.location.search).get('preview') === 'upload';
}

const UserListStep = ({ contractId, onNext, prefilledData }) => {
    const uploadPreview = isUploadPreview();
    const [uploading, setUploading] = useState(false);
    const [phase, setPhase] = useState(
        uploadPreview
            ? 'upload'
            : (prefilledData?.licensePlan?.mixed || (prefilledData?.licensePlan?.users || []).length
                ? 'licenses'
                : 'checking')
    ); // checking | upload | licenses | done
    const [error, setError] = useState(null);
    const [errorIssues, setErrorIssues] = useState([]);
    const [spreadsheetUrl, setSpreadsheetUrl] = useState('');

    const [passwordMode, setPasswordMode] = useState('temporal'); // 'manual', 'temporal', 'secure'
    const [mixedLicenses, setMixedLicenses] = useState(
        uploadPreview || Boolean(prefilledData?.licensePlan?.mixed)
    );
    const success = phase === 'done';

    const clientDomain = prefilledData?.domain || prefilledData?.dominio || 'Cliente';
    const clientLabel = prefilledData?.clientName || prefilledData?.nombre_comercial || prefilledData?.razon_social || clientDomain;

    useEffect(() => {
        if (!contractId) {
            setPhase((prev) => (prev === 'checking' ? 'upload' : prev));
            return undefined;
        }
        if (uploadPreview) return undefined;
        let cancelled = false;
        getPublicContractLicensePlan(contractId)
            .then((res) => {
                if (cancelled) return;
                setMixedLicenses(Boolean(res?.mixed));
                if (res?.mixed && (res.users || []).length) setPhase('licenses');
                else setPhase((prev) => (prev === 'licenses' || prev === 'done' ? prev : 'upload'));
            })
            .catch(() => {
                if (!cancelled) setPhase((prev) => (prev === 'checking' ? 'upload' : prev));
            });
        return () => { cancelled = true; };
    }, [contractId, uploadPreview]);

    const handleDownload = () => {
        downloadUserTemplate(clientDomain, passwordMode === 'manual', contractId);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        // Permite re-intentar con el mismo archivo tras un error
        e.target.value = '';

        setUploading(true);
        setError(null);
        setErrorIssues([]);

        // Pre-validación del CSV en Frontend usando FileReader
        const reader = new FileReader();
        reader.onload = async (event) => {
            const text = event.target.result;
            const firstLine = text.split('\n')[0].toLowerCase();
            const hasPassword = firstLine.includes('password') || firstLine.includes('contraseña');

            if (passwordMode === 'manual' && !hasPassword) {
                setError('Elegiste "Yo las agregaré", pero el archivo no tiene la columna "Password".');
                setUploading(false);
                return;
            }

            if (passwordMode !== 'manual' && hasPassword) {
                setError('Elegiste generación automática, pero el archivo incluye una columna de contraseñas. Por favor usa la plantilla sin contraseñas.');
                setUploading(false);
                return;
            }

            // Si pasa la validación, subimos al backend
            try {
                const res = await processUserList(contractId, file, passwordMode);
                setSpreadsheetUrl(res.spreadsheetUrl);
                if (res.mixed || res.licensePlan) {
                    setPhase('licenses');
                } else {
                    setPhase('done');
                }
            } catch (err) {
                console.error(err);
                setError(err.message || 'Error al procesar el archivo CSV.');
                setErrorIssues(Array.isArray(err.issues) ? err.issues : []);
            } finally {
                setUploading(false);
            }
        };
        
        reader.onerror = () => {
             setError("Error al leer el archivo localmente.");
             setUploading(false);
        };

        // Solo necesitamos leer un pedazo pequeño para validar los encabezados, 
        // pero readAsText completo funciona bien para archivos pequeños.
        reader.readAsText(file.slice(0, 1024)); // Leer los primeros 1KB es suficiente para los headers
    };

    return (
        <div className="user-list-step-integrated">
            {/* Branding Superior */}
            <div className="plex-branded-header">
                <img src="/assets/logo-plexarian.svg" alt="Plexarian Cloud" className="plex-hero-logo" />
            </div>

            {/* Aviso de Continuidad */}
            <div className="continuity-banner">
                <div className="banner-icon"><RefreshCw size={18} /></div>
                <div className="banner-text">
                    {phase === 'licenses'
                        ? <><strong>No es obligatorio ahora:</strong> marca a quien puedas y guarda, o elige “Lo haré después”. Cuando lo tengan claro, vuelvan a abrir este mismo enlace.</>
                        : <><strong>Tómate tu tiempo:</strong> puedes cerrar esta ventana y regresar más tarde con el mismo enlace. Tu progreso se guarda.</>}
                </div>
            </div>

            <div className="step-inner-content">
                {phase !== 'done' && (
                    <>
                <h2 className="step-title-plex">
                    <span>
                    {phase === 'licenses'
                        ? 'Indica el tipo de licencia de cada persona'
                        : phase === 'checking'
                            ? 'Un momento…'
                            : 'Carga de usuarios'}
                    </span>
                    {phase !== 'checking' && (
                    <span className="step-title-brands">
                        <img className="step-brand-gws" src="/assets/Google_Workspace_logo.svg" alt="Google Workspace" />
                        <img className="step-brand-ge" src="/assets/Gemini_SparkIcon_4C.png" alt="Gemini" />
                    </span>
                    )}
                </h2>
                {phase !== 'checking' && (
                <p className="step-subtitle-plex">
                    {phase === 'licenses'
                        ? 'La lista ya está cargada. Ahora indica qué licencia lleva cada persona.'
                        : <>Sube el nombre, apellido y correo de cada colaborador de <strong className="step-client-name">{clientLabel}</strong>.</>}
                </p>
                )}
                {mixedLicenses && phase === 'upload' && (
                    <div className="license-how">
                        <h3>En el siguiente paso agregas las licencias</h3>
                        <p>Este archivo solo lleva a las personas. Al subirlo, verás la lista y ahí indicas qué tipo de licencia usa cada una.</p>
                        <p>Son dos marcas, por separado:</p>
                        <ol>
                            <li>
                                <strong>El plan</strong>
                                <span>Starter, Standard o Plus. Todos empiezan en el más bajo.</span>
                            </li>
                            <li>
                                <strong>Gemini</strong>
                                <span>Se prende aparte. Una persona puede llevar plan y Gemini a la vez, por ejemplo Plus y Gemini.</span>
                            </li>
                        </ol>
                    </div>
                )}
                {mixedLicenses && phase === 'licenses' && (
                    <div className="license-how">
                        <h3>Dos marcas por persona</h3>
                        <p>Elige el plan y, si le toca, prende Gemini. Se pueden dejar los dos.</p>
                        <ol>
                            <li>
                                <strong>El plan</strong>
                                <span>Starter, Standard o Plus. Todos empiezan en el más bajo. Cámbialo solo si esa persona lleva otro.</span>
                            </li>
                            <li>
                                <strong>Gemini</strong>
                                <span>Es un botón aparte. Préndelo en quien lo lleve.</span>
                            </li>
                        </ol>
                    </div>
                )}
                    </>
                )}

                {phase === 'upload' && (
                    <div className="password-strategy-selector">
                        <h4 className="strategy-title">¿Cómo quieres gestionar las contraseñas?</h4>
                        <div className="strategy-options">
                            <label className={`strategy-card ${passwordMode === 'manual' ? 'active' : ''}`}>
                                <input type="radio" name="passwordMode" value="manual" checked={passwordMode === 'manual'} onChange={() => setPasswordMode('manual')} />
                                <div className="card-dot"></div>
                                <div className="card-info">
                                    <strong>Yo las agregaré</strong>
                                    <span>Se agregará la columna "Password" a la plantilla. Google pide 8 a 100 caracteres, sin ñ ni acentos, y sin el usuario del correo.</span>
                                </div>
                            </label>
                            
                            <label className={`strategy-card ${passwordMode === 'temporal' ? 'active' : ''}`}>
                                <input type="radio" name="passwordMode" value="temporal" checked={passwordMode === 'temporal'} onChange={() => setPasswordMode('temporal')} />
                                <div className="card-dot"></div>
                                <div className="card-info">
                                    <strong>Usar Temporales</strong>
                                    <span>Todos usarán "Temporal12345" (forzar cambio al entrar).</span>
                                </div>
                            </label>

                            <label className={`strategy-card ${passwordMode === 'secure' ? 'active' : ''}`}>
                                <input type="radio" name="passwordMode" value="secure" checked={passwordMode === 'secure'} onChange={() => setPasswordMode('secure')} />
                                <div className="card-dot"></div>
                                <div className="card-info">
                                    <strong>Generar Seguras</strong>
                                    <span>Creamos contraseñas de 12 dígitos, seguras y amigables. <br/><small style={{color: '#8A2BE2'}}>El equipo te las entregará en tu implementación.</small></span>
                                </div>
                            </label>
                        </div>
                    </div>
                )}

                {phase === 'licenses' ? (
                    <div className="license-plan-client">
                        <LicensePlanPanel
                            mode="client"
                            contract={{ id: contractId }}
                            showToast={(msg, type) => {
                                if (type === 'error') {
                                    setError(msg);
                                    setErrorIssues([]);
                                }
                            }}
                            onClientDone={() => {
                                setError(null);
                                setPhase('done');
                            }}
                        />
                        <button
                            type="button"
                            className="license-plan-client__back"
                            onClick={() => setPhase('upload')}
                        >
                            Re-subir CSV
                        </button>
                    </div>
                ) : phase === 'checking' ? (
                    <p className="step-subtitle-plex">Estamos abriendo tu lista…</p>
                ) : !success ? (
                    <div className="upload-workflow-plex">
                        {/* Reglas de Carga */}
                        <div className="rules-glass-panel">
                            <h4 className="rules-header-plex"><AlertCircle size={18} /> Reglas Importantes</h4>
                            <ul className="rules-list-plex">
                                <li><strong>Campos obligatorios</strong>: Nombre, Apellido y Correo.</li>
                                {mixedLicenses && (
                                    <li><strong>Licencias después</strong>: el tipo de licencia se elige en el siguiente paso, no en el archivo.</li>
                                )}
                                <li><strong>Dominio correcto</strong>: todos los correos deben ser @{clientDomain}.</li>
                                {passwordMode === 'manual' && (
                                    <li><strong>Contraseñas de Google</strong>: mínimo 8 caracteres, máximo 100, sin ñ ni acentos, y sin el usuario del correo. Si alguna no cumple, te decimos cuál y por qué.</li>
                                )}
                                <li><strong>Formato .CSV</strong>: guarda el archivo como valor separado por comas.</li>
                            </ul>
                        </div>

                        <div className="action-grid-plex">
                            <div className="action-item-plex">
                                <div className="action-header-plex">
                                    <div className="icon-wrap"><Download size={22} /></div>
                                    <h4>1. Descarga la Plantilla</h4>
                                </div>
                                <p>Nombre, apellido y correo de cada persona.</p>
                                <button onClick={handleDownload} className="btn-plex-action pink-glass">
                                    Descargar Plantilla ({clientDomain})
                                </button>
                            </div>

                            <div className="action-item-plex">
                                <div className="action-header-plex">
                                    <div className="icon-wrap"><Upload size={22} /></div>
                                    <h4>2. Sube tu Lista</h4>
                                </div>
                                <p>{mixedLicenses ? 'Al subirla pasas al paso donde marcas el tipo de licencia de cada persona.' : 'Una vez completa, procésala aquí.'}</p>
                                <label className={`btn-plex-action purple-glass ${uploading ? 'loading' : ''}`}>
                                    {uploading ? <RefreshCw className="spin" /> : <Upload size={18} />}
                                    {uploading ? 'Procesando...' : 'Seleccionar Archivo'}
                                    <input type="file" accept=".csv" onChange={handleFileUpload} style={{ display: 'none' }} disabled={uploading} />
                                </label>
                            </div>
                        </div>

                        {error && (
                            <div className="error-box-plex">
                                <ShieldAlert size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                <div className="error-box-body">
                                    <strong>{error}</strong>
                                    {errorIssues.length > 0 && (
                                        <ul className="error-issues-list">
                                            {errorIssues.slice(0, 20).map((issue, idx) => (
                                                <li key={`${issue.line || 'x'}-${issue.field || idx}-${idx}`}>
                                                    {issue.message}
                                                </li>
                                            ))}
                                            {errorIssues.length > 20 && (
                                                <li>… y {errorIssues.length - 20} problemas más.</li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="success-glass-premium">
                        <div className="success-icon-vibe">
                            <CheckCircle size={48} color="#00E676" />
                        </div>
                        <h2 className="success-title-vibe">Lista recibida</h2>
                        <p className="success-text-vibe">
                            Ya tenemos tu lista. Si más adelante quieres indicar quién lleva cada tipo de licencia (incluido Gemini, si lo contrataron), entra otra vez con este mismo enlace. No es urgente: Plexarian lo aplica en la implementación.
                        </p>

                        <div className="success-actions-vibe">
                            <button 
                                onClick={() => window.location.href = 'https://onboarding.plexarian.com'} 
                                className="btn-vibe primary-vibe"
                            >
                                Continuar
                            </button>
                        </div>

                        {(passwordMode === 'secure' || passwordMode === 'temporal') && (
                            <div className="creds-info-vibe">
                                <Shield size={14} />
                                <span>Las credenciales finales serán entregadas por el equipo técnico durante tu sesión de implementación.</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <style>{`
                .user-list-step-integrated {
                    width: 100%;
                    text-align: left;
                    color: #1a0b23;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .plex-branded-header {
                    margin-bottom: 40px;
                    text-align: center;
                }

                .plex-hero-logo {
                    height: 100px;
                    filter: brightness(1); /* Logo original para fondo claro */
                }

                .continuity-banner {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    background: rgba(255, 51, 100, 0.05);
                    border: 1px dashed #FF3364;
                    border-radius: 12px;
                    padding: 15px 20px;
                    margin-bottom: 30px;
                    color: #331D40;
                }
                .banner-icon { color: #FF3364; }
                .banner-text { font-size: 0.9rem; line-height: 1.4; }

                .step-inner-content { width: 100%; }
                .license-plan-client { width: 100%; text-align: left; }
                .license-plan-client__back {
                    margin-top: 16px; background: none; border: none; padding: 0;
                    color: #64748B; font-size: 0.8rem; font-weight: 600; cursor: pointer;
                    text-decoration: underline; font-family: inherit;
                }
                .step-title-plex {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px 28px;
                    flex-wrap: wrap;
                    font-size: 1.8rem;
                    font-weight: 800;
                    margin: 0 0 8px 0;
                    color: #331D40;
                }
                .step-title-brands {
                    display: inline-flex;
                    align-items: center;
                    gap: 14px;
                    flex-shrink: 0;
                }
                .step-brand-gws { height: 22px; width: auto; display: block; }
                .step-brand-ge { height: 30px; width: auto; display: block; }
                .step-subtitle-plex { color: #64748B; margin-bottom: 22px; font-size: 1rem; line-height: 1.5; }
                .step-client-name {
                    color: #FF3364;
                    font-size: 1.55rem;
                    font-weight: 800;
                    letter-spacing: -0.02em;
                    line-height: 1.15;
                }
                .license-how {
                    background: #fff;
                    border: 1px solid #F3D0DA;
                    border-left: 4px solid #FF3364;
                    border-radius: 16px;
                    padding: 18px 20px 16px;
                    margin: 0 0 28px;
                }
                .license-how h3 {
                    margin: 0 0 8px;
                    color: #331D40;
                    font-size: 1.05rem;
                    font-weight: 800;
                }
                .license-how p {
                    margin: 0 0 12px;
                    color: #475569;
                    line-height: 1.45;
                    font-size: 0.95rem;
                }
                .license-how ol {
                    margin: 0;
                    padding-left: 1.15rem;
                    color: #331D40;
                }
                .license-how li { margin-bottom: 10px; line-height: 1.35; }
                .license-how li:last-child { margin-bottom: 0; }
                .license-how li span {
                    display: block;
                    margin-top: 2px;
                    color: #64748B;
                    font-weight: 500;
                    font-size: 0.9rem;
                }

                .password-strategy-selector {
                    margin-bottom: 35px;
                    width: 100%;
                }
                .strategy-title {
                    font-size: 1rem;
                    color: #475569;
                    margin-bottom: 15px;
                    font-weight: 600;
                }
                .strategy-options {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 15px;
                }
                .strategy-card {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 15px;
                    background: #fff;
                    border: 2px solid #e2e8f0;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 0.3s;
                    position: relative;
                }
                .strategy-card:hover {
                    border-color: #FF3364;
                    background: #FFF1F4;
                }
                .strategy-card.active {
                    border-color: #FF3364;
                    background: #FFF1F4;
                    box-shadow: 0 10px 25px rgba(255, 51, 100, 0.1);
                }
                .strategy-card input {
                    position: absolute;
                    opacity: 0;
                    cursor: pointer;
                }
                .card-dot {
                    width: 18px;
                    height: 18px;
                    border: 2px solid #cbd5e1;
                    border-radius: 50%;
                    flex-shrink: 0;
                    margin-top: 2px;
                    position: relative;
                    transition: 0.3s;
                }
                .strategy-card.active .card-dot {
                    border-color: #FF3364;
                }
                .strategy-card.active .card-dot::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 10px;
                    height: 10px;
                    background: #FF3364;
                    border-radius: 50%;
                }
                .card-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .card-info strong {
                    font-size: 0.95rem;
                    color: #331D40;
                }
                .card-info span {
                    font-size: 0.8rem;
                    color: #64748B;
                    line-height: 1.3;
                }

                .rules-glass-panel {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 16px;
                    padding: 20px;
                    margin-bottom: 30px;
                }
                .rules-header-plex { display: flex; align-items: center; gap: 10px; color: #FF3364; margin: 0 0 12px 0; font-weight: 700; }
                .rules-list-plex { margin: 0; padding-left: 20px; color: #475569; font-size: 0.9rem; }
                .rules-list-plex li { margin-bottom: 6px; }

                .action-grid-plex { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .action-item-plex {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 20px;
                    padding: 25px;
                    transition: all 0.3s;
                }
                .action-item-plex:hover { border-color: #FF3364; box-shadow: 0 10px 30px rgba(0,0,0,0.05); }

                .action-header-plex { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
                .icon-wrap { width: 40px; height: 40px; background: #FFF1F4; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #FF3364; }
                .action-header-plex h4 { margin: 0; font-weight: 700; color: #331D40; }
                .action-item-plex p { color: #64748B; font-size: 0.85rem; margin-bottom: 20px; }

                .btn-plex-action {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    padding: 12px 20px;
                    border-radius: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: 0.3s;
                    border: 1px solid transparent;
                    width: 100%;
                    font-size: 0.9rem;
                    text-decoration: none;
                }

                .pink-glass { background: #FF3364; color: white; }
                .pink-glass:hover { background: #D41D4D; box-shadow: 0 8px 20px rgba(255,51,100,0.3); }

                .purple-glass { background: #331D40; color: white; }
                .purple-glass:hover { background: #1a0b23; box-shadow: 0 8px 20px rgba(51,29,64,0.3); }

                .error-box-plex { background: #FEF2F2; border: 1px solid #FCA5A5; color: #B91C1C; padding: 14px 16px; border-radius: 10px; margin-top: 20px; display: flex; align-items: flex-start; gap: 10px; font-size: 0.85rem; }
                .error-box-body { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
                .error-box-body strong { font-weight: 700; line-height: 1.4; }
                .error-issues-list { margin: 0; padding-left: 18px; color: #991B1B; line-height: 1.45; }
                .error-issues-list li { margin-bottom: 4px; }
                .success-state-plex { text-align: center; padding: 30px 0; }
                .success-glass-premium {
                    background: #ffffff;
                    border-radius: 24px;
                    padding: 60px 40px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 400px;
                    border: 1px solid #E2E8F0;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
                    position: relative;
                }

                .success-icon-vibe {
                    width: 100px;
                    height: 100px;
                    background: rgba(16, 185, 129, 0.1);
                    border: 1px solid rgba(16, 185, 129, 0.3);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #10B981;
                    margin-bottom: 30px;
                    animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }

                @keyframes scaleIn {
                    from { transform: scale(0); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }

                .success-title-vibe {
                    font-size: 2.2rem;
                    font-weight: 800;
                    margin-bottom: 15px;
                    color: #331D40;
                }

                .success-text-vibe {
                    color: #64748B;
                    max-width: 400px;
                    line-height: 1.6;
                    margin-bottom: 40px;
                    font-size: 1.1rem;
                }

                .success-actions-vibe {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                    width: 100%;
                    max-width: 300px;
                }

                .btn-vibe {
                    padding: 16px 24px;
                    border-radius: 14px;
                    font-weight: 800;
                    font-size: 1rem;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    text-decoration: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    border: none;
                }

                .primary-vibe {
                    background: #2DDF8C;
                    color: #0f0514;
                    box-shadow: 0 4px 15px rgba(45, 223, 140, 0.3);
                }
                .primary-vibe:hover {
                    background: #24b370;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(45, 223, 140, 0.4);
                }

                .secondary-vibe {
                    background: rgba(255, 255, 255, 0.05);
                    color: #ffffff;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                .secondary-vibe:hover {
                    background: rgba(255, 255, 255, 0.1);
                    border-color: rgba(255, 255, 255, 0.2);
                    transform: translateY(-2px);
                }

                .creds-info-vibe {
                    margin-top: 40px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 15px 20px;
                    background: #F8FAFC;
                    border: 1px solid #E2E8F0;
                    border-radius: 12px;
                    color: #64748B;
                    font-size: 0.85rem;
                    max-width: 450px;
                    text-align: left;
                }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

                @media (max-width: 800px) {
                    .strategy-options { grid-template-columns: 1fr; }
                    .success-glass-premium { padding: 40px 20px; }
                }

                @media (max-width: 600px) {
                    .action-grid-plex { grid-template-columns: 1fr; }
                }
            `}</style>
        </div>
    );
};

export default UserListStep;
